<template>
  <div class="profile-view p-6 space-y-8 max-w-4xl mx-auto">
    <!-- Header Section -->
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
      <div>
        <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-100">Profile Settings</h1>
        <p class="mt-2 text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>
    </div>

    <!-- Success Notification Banner -->
    <div
      v-if="showVerificationSuccess"
      class="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 animate-fade-in"
    >
      <div class="flex items-center">
        <CheckIcon class="w-5 h-5 text-green-600 dark:text-green-400 mr-2" />
        <div class="flex-1">
          <h3 class="text-sm font-medium text-green-800 dark:text-green-200">
            {{ verificationSuccessTitle }}
          </h3>
          <p class="text-sm text-green-700 dark:text-green-300 mt-1">
            {{ verificationSuccessMessage }}
          </p>
        </div>
        <button
          class="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
          @click="hideVerificationSuccess"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <div class="mt-4 lg:mt-0">
        <button
          :disabled="!hasUnsavedChanges || isSaving"
          class="btn-primary"
          @click="saveAllSettings"
        >
          <span v-if="isSaving">Saving...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
    </div>

    <!-- Profile Information Card -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Profile Information</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Your account information from Telegram
        </p>
      </div>
      <div class="card-body">
        <div class="flex items-center space-x-6">
          <!-- Profile Picture -->
          <div class="flex-shrink-0">
            <div class="relative">
              <img
                v-if="userProfile?.photoUrl"
                :src="userProfile.photoUrl"
                :alt="userDisplayName"
                class="w-20 h-20 rounded-full object-cover"
              />
              <div
                v-else
                class="w-20 h-20 rounded-full bg-primary-600 flex items-center justify-center"
              >
                <span class="text-2xl font-medium text-white">
                  {{ userInitials }}
                </span>
              </div>

              <!-- Upload button overlay -->
              <button
                class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-200"
                @click="triggerPhotoUpload"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>

              <!-- Hidden file input -->
              <input
                ref="photoUploadInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handlePhotoUpload"
              />
            </div>
          </div>

          <!-- Profile Details -->
          <div class="flex-1 space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  First Name
                </label>
                <input
                  v-model="profileForm.firstName"
                  type="text"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  :placeholder="userProfile?.firstName || 'First name'"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Last Name
                </label>
                <input
                  v-model="profileForm.lastName"
                  type="text"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  :placeholder="userProfile?.lastName || 'Last name'"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Username
              </label>
              <div class="mt-1 flex">
                <span
                  class="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-sm"
                >
                  @
                </span>
                <input
                  v-model="profileForm.username"
                  type="text"
                  class="flex-1 block w-full rounded-none rounded-r-md border border-gray-300 dark:border-gray-600 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
                  :placeholder="userProfile?.username || 'username'"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Telegram ID
                </label>
                <input
                  :value="userProfile?.telegramId"
                  type="text"
                  disabled
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-gray-50 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                />
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Your Telegram ID cannot be changed
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Account Tier
                </label>
                <div class="mt-1 flex items-center space-x-2">
                  <span class="text-2xl">{{ currentTierInfo.icon }}</span>
                  <div>
                    <div class="font-medium text-gray-900 dark:text-gray-100">
                      Tier {{ userTier }} - {{ currentTierInfo.name }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400">
                      {{ currentTierInfo.description }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Verification Status -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Verification
                </label>
                <div class="mt-1 flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                    <span
                      class="text-sm transition-all duration-300"
                      :class="
                        userProfile?.emailVerified
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-gray-400'
                      "
                    >
                      <span v-if="userProfile?.emailVerified" class="flex items-center">
                        <CheckIcon class="w-4 h-4 mr-1" />
                        Verified
                      </span>
                      <span v-else>Not verified</span>
                    </span>
                    <div
                      v-if="showEmailVerificationSuccess"
                      class="text-xs text-green-600 dark:text-green-400 animate-fade-in"
                    >
                      {{ emailVerificationMessage }}
                    </div>
                  </div>
                  <button
                    v-if="!userProfile?.emailVerified && canVerifyEmail"
                    class="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200"
                    @click="showEmailVerificationModal = true"
                  >
                    Verify
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Phone Verification
                </label>
                <div class="mt-1 flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                    <span
                      class="text-sm transition-all duration-300"
                      :class="
                        userProfile?.phoneVerified
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-500 dark:text-gray-400'
                      "
                    >
                      <span v-if="userProfile?.phoneVerified" class="flex items-center">
                        <CheckIcon class="w-4 h-4 mr-1" />
                        Verified
                      </span>
                      <span v-else>Not verified</span>
                    </span>
                    <div
                      v-if="showPhoneVerificationSuccess"
                      class="text-xs text-green-600 dark:text-green-400 animate-fade-in"
                    >
                      {{ phoneVerificationMessage }}
                    </div>
                  </div>
                  <button
                    v-if="!userProfile?.phoneVerified && canVerifyPhone"
                    class="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors duration-200"
                    @click="showPhoneVerificationModal = true"
                  >
                    Verify
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tier Progress Card -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Tier Progress</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Track your progress and unlock new features
        </p>
      </div>
      <div class="card-body">
        <!-- Current Tier Status -->
        <div class="flex items-center justify-between mb-6">
          <div class="flex items-center space-x-4">
            <div
              class="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-2xl"
            >
              {{ currentTierInfo.icon }}
            </div>
            <div>
              <h4 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {{ currentTierInfo.name }}
              </h4>
              <p class="text-gray-500 dark:text-gray-400">
                {{ currentTierInfo.description }}
              </p>
            </div>
          </div>
          <div class="text-right">
            <div class="text-2xl font-bold text-gray-900 dark:text-gray-100">{{ userTier }}/7</div>
            <div class="text-sm text-gray-500 dark:text-gray-400">Tier Level</div>
          </div>
        </div>

        <!-- Overall Progress -->
        <div class="mb-6">
          <div class="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
            <span>Overall Progress</span>
            <span>{{ Math.round((userTier / 7) * 100) }}%</span>
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
            <div
              class="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500"
              :style="{ width: `${(userTier / 7) * 100}%` }"
            />
          </div>
        </div>

        <!-- Current Tier Benefits -->
        <div class="mb-6">
          <h5 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
            Your Current Benefits
          </h5>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div
              v-for="benefit in currentTierInfo.benefits"
              :key="benefit"
              class="flex items-center space-x-2"
            >
              <CheckIcon class="h-4 w-4 text-green-500 flex-shrink-0" />
              <span class="text-sm text-gray-600 dark:text-gray-300">{{ benefit }}</span>
            </div>
          </div>
        </div>

        <!-- Next Tier Requirements -->
        <div v-if="nextTierInfo" class="border-t pt-6">
          <div class="flex items-center space-x-4 mb-4">
            <div
              class="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xl"
            >
              {{ nextTierInfo.icon }}
            </div>
            <div>
              <h5 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Next: {{ nextTierInfo.name }}
              </h5>
              <p class="text-gray-500 dark:text-gray-400">
                {{ nextTierInfo.description }}
              </p>
            </div>
          </div>

          <!-- Requirements Checklist -->
          <div class="space-y-3 mb-4">
            <h6 class="text-sm font-medium text-gray-900 dark:text-gray-100">
              Requirements to Unlock
            </h6>
            <div
              v-for="requirement in nextTierInfo.requirements"
              :key="requirement.key"
              class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div class="flex items-center space-x-3">
                <div
                  class="w-6 h-6 rounded-full flex items-center justify-center"
                  :class="
                    requirement.completed
                      ? 'bg-green-100 dark:bg-green-900'
                      : 'bg-gray-100 dark:bg-gray-700'
                  "
                >
                  <CheckIcon
                    v-if="requirement.completed"
                    class="h-4 w-4 text-green-600 dark:text-green-400"
                  />
                  <span v-else class="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full" />
                </div>
                <div>
                  <div class="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ requirement.title }}
                  </div>
                  <div class="text-xs text-gray-500 dark:text-gray-400">
                    {{ requirement.description }}
                  </div>
                </div>
              </div>
              <button
                v-if="!requirement.completed && requirement.actionable"
                class="px-3 py-1 text-sm font-medium rounded-md border border-transparent"
                :class="
                  requirement.actionType === 'verify'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-600 text-white hover:bg-gray-700'
                "
                @click="handleRequirementAction(requirement)"
              >
                {{ requirement.actionLabel }}
              </button>
              <span
                v-else-if="requirement.completed"
                class="px-3 py-1 text-sm font-medium text-green-600 dark:text-green-400"
              >
                ✓ Complete
              </span>
              <span v-else class="px-3 py-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                {{ requirement.statusText }}
              </span>
            </div>
          </div>

          <!-- Progress to next tier -->
          <div>
            <div class="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
              <span>Progress to {{ nextTierInfo.name }}</span>
              <span>{{ nextTierProgress }}%</span>
            </div>
            <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                class="bg-blue-500 h-2 rounded-full transition-all duration-500"
                :style="{ width: `${nextTierProgress}%` }"
              />
            </div>
          </div>
        </div>

        <!-- Maximum Tier Reached -->
        <div v-else class="bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-white">
          <div class="flex items-center space-x-4">
            <div
              class="w-12 h-12 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-2xl"
            >
              🏆
            </div>
            <div>
              <h4 class="text-xl font-semibold">Congratulations!</h4>
              <p class="text-purple-100">
                You've reached the maximum tier level. Enjoy all premium features!
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preferences Card -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Preferences</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Customize your experience</p>
      </div>
      <div class="card-body space-y-6">
        <!-- Theme Selection -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Theme
          </label>
          <div class="grid grid-cols-3 gap-3">
            <label
              v-for="theme in themes"
              :key="theme.value"
              class="relative flex cursor-pointer rounded-lg border p-4 focus:outline-none"
              :class="{
                'border-primary-600 ring-2 ring-primary-600': preferences.theme === theme.value,
                'border-gray-300 dark:border-gray-600': preferences.theme !== theme.value,
              }"
            >
              <input
                v-model="preferences.theme"
                :value="theme.value"
                type="radio"
                class="sr-only"
              />
              <div class="flex flex-1 flex-col">
                <div class="flex items-center">
                  <component
                    :is="theme.icon"
                    class="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2"
                  />
                  <span class="block text-sm font-medium text-gray-900 dark:text-gray-100">
                    {{ theme.label }}
                  </span>
                </div>
                <span class="mt-1 flex items-center text-sm text-gray-500 dark:text-gray-400">
                  {{ theme.description }}
                </span>
              </div>
            </label>
          </div>
        </div>

        <!-- Language Selection -->
        <div>
          <label
            for="language-select"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Language
          </label>
          <select
            id="language-select"
            v-model="preferences.language"
            class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="it">Italiano</option>
            <option value="pt">Português</option>
            <option value="ru">Русский</option>
            <option value="zh">中文</option>
            <option value="ja">日本語</option>
            <option value="ko">한국어</option>
          </select>
        </div>

        <!-- UI Preferences -->
        <div class="space-y-4">
          <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">Interface</h4>

          <div class="space-y-3">
            <label class="flex items-center">
              <input
                v-model="preferences.ui.compactMode"
                type="checkbox"
                class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span class="ml-2 text-sm text-gray-700 dark:text-gray-300"> Compact mode </span>
            </label>

            <label class="flex items-center">
              <input
                v-model="preferences.ui.showAdvancedFeatures"
                type="checkbox"
                class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Show advanced features
              </span>
            </label>

            <label class="flex items-center">
              <input
                v-model="preferences.ui.autoSaveInputs"
                type="checkbox"
                class="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Auto-save tool inputs
              </span>
            </label>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Default tool view
            </label>
            <div class="flex space-x-3">
              <label class="flex items-center">
                <input
                  v-model="preferences.ui.defaultToolView"
                  value="grid"
                  type="radio"
                  class="border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Grid</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="preferences.ui.defaultToolView"
                  value="list"
                  type="radio"
                  class="border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">List</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notifications Card -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Notifications</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Choose what notifications you want to receive
        </p>
      </div>
      <div class="card-body space-y-4">
        <label class="flex items-start">
          <input
            v-model="preferences.notifications.telegram"
            type="checkbox"
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div class="ml-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Telegram notifications
            </span>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Receive notifications via Telegram bot
            </p>
          </div>
        </label>

        <label class="flex items-start">
          <input
            v-model="preferences.notifications.apiLimits"
            type="checkbox"
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div class="ml-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              API limit warnings
            </span>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Get notified when approaching API limits
            </p>
          </div>
        </label>

        <label class="flex items-start">
          <input
            v-model="preferences.notifications.securityAlerts"
            type="checkbox"
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div class="ml-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Security alerts
            </span>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Important security and account notifications
            </p>
          </div>
        </label>

        <label class="flex items-start">
          <input
            v-model="preferences.notifications.featureUpdates"
            type="checkbox"
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div class="ml-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Feature updates
            </span>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Learn about new features and improvements
            </p>
          </div>
        </label>
      </div>
    </div>

    <!-- Privacy Card -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Privacy</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">
          Control your data and privacy settings
        </p>
      </div>
      <div class="card-body space-y-4">
        <label class="flex items-start">
          <input
            v-model="preferences.privacy.shareUsageStatistics"
            type="checkbox"
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div class="ml-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Share usage statistics
            </span>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Help improve the service by sharing anonymous usage data
            </p>
          </div>
        </label>

        <label class="flex items-start">
          <input
            v-model="preferences.privacy.allowAnalytics"
            type="checkbox"
            class="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <div class="ml-3">
            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
              Allow analytics
            </span>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Enable analytics to help us understand how you use the platform
            </p>
          </div>
        </label>
      </div>
    </div>

    <!-- Usage Statistics Card -->
    <div class="card">
      <div class="card-header">
        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Usage Statistics</h3>
        <p class="text-sm text-gray-500 dark:text-gray-400">Your current usage and quotas</p>
      </div>
      <div class="card-body">
        <div class="space-y-6">
          <QuotaBar
            label="API Calls (Hourly)"
            :current="quotaUsage?.apiCalls?.currentHour || 0"
            :limit="quotaUsage?.apiCalls?.limit || 100"
            color="blue"
          />
          <QuotaBar
            label="Tool Executions (Hourly)"
            :current="quotaUsage?.toolExecutions?.currentHour || 0"
            :limit="quotaUsage?.toolExecutions?.limit || 50"
            color="primary"
          />
          <QuotaBar
            label="Vector Documents"
            :current="quotaUsage?.vectorDocuments?.current || 0"
            :limit="quotaUsage?.vectorDocuments?.limit || 1000"
            color="purple"
          />
          <QuotaBar
            label="Storage"
            :current="quotaUsage?.storage?.currentBytes || 0"
            :limit="quotaUsage?.storage?.limitBytes || 1073741824"
            :format-value="formatBytes"
            color="green"
          />
        </div>
      </div>
    </div>

    <!-- Danger Zone -->
    <div class="card border-error-200 dark:border-error-800">
      <div class="card-header bg-error-50 dark:bg-error-900/20">
        <h3 class="text-lg font-medium text-error-900 dark:text-error-100">Danger Zone</h3>
        <p class="text-sm text-error-700 dark:text-error-300">
          Irreversible and destructive actions
        </p>
      </div>
      <div class="card-body space-y-4">
        <div
          class="flex items-center justify-between p-4 border border-error-200 dark:border-error-800 rounded-lg"
        >
          <div>
            <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
              Export Account Data
            </h4>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Download a copy of all your account data
            </p>
          </div>
          <button class="btn-secondary" @click="exportAccountData">Export Data</button>
        </div>

        <div
          class="flex items-center justify-between p-4 border border-error-200 dark:border-error-800 rounded-lg"
        >
          <div>
            <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">Delete Account</h4>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Permanently delete your account and all associated data
            </p>
          </div>
          <button
            class="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-error-600 hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500"
            @click="showDeleteConfirmation = true"
          >
            Delete Account
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteConfirmation"
      class="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          @click="showDeleteConfirmation = false"
        />

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div
          class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
        >
          <div class="sm:flex sm:items-start">
            <div
              class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-error-100 dark:bg-error-900/50 sm:mx-0 sm:h-10 sm:w-10"
            >
              <svg
                class="h-6 w-6 text-error-600 dark:text-error-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                Delete Account
              </h3>
              <div class="mt-2">
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to delete your account? This action cannot be undone and all
                  your data will be permanently removed.
                </p>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <button
              :disabled="isDeleting"
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-error-600 text-base font-medium text-white hover:bg-error-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-error-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              @click="deleteAccount"
            >
              <span v-if="isDeleting">Deleting...</span>
              <span v-else>Delete Account</span>
            </button>
            <button
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              @click="showDeleteConfirmation = false"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Verification Placeholder Modals -->
    <!-- Email Verification Modal -->
    <EmailVerificationModal
      :is-open="showEmailVerificationModal"
      @close="showEmailVerificationModal = false"
      @verified="handleEmailVerified"
    />

    <!-- Phone Verification Modal -->
    <PhoneVerificationModal
      :is-open="showPhoneVerificationModal"
      @close="showPhoneVerificationModal = false"
      @verified="handlePhoneVerified"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { CheckIcon } from '@heroicons/vue/24/outline';

import { storageService } from '@/services/storage';
import QuotaBar from '@/components/ui/QuotaBar.vue';
import EmailVerificationModal from '@/components/EmailVerificationModal.vue';
import PhoneVerificationModal from '@/components/PhoneVerificationModal.vue';

const router = useRouter();
const authStore = useAuthStore();

// State
const isSaving = ref(false);
const isDeleting = ref(false);
const showDeleteConfirmation = ref(false);
const hasUnsavedChanges = ref(false);
const photoUploadInput = ref<HTMLInputElement | null>(null);
const showEmailVerificationModal = ref(false);
const showPhoneVerificationModal = ref(false);
const showEmailVerificationSuccess = ref(false);
const emailVerificationMessage = ref('');
const showPhoneVerificationSuccess = ref(false);
const phoneVerificationMessage = ref('');
const showVerificationSuccess = ref(false);
const verificationSuccessTitle = ref('');
const verificationSuccessMessage = ref('');

// Form data
const profileForm = reactive({
  firstName: '',
  lastName: '',
  username: '',
});

const preferences = reactive({
  theme: 'system' as 'light' | 'dark' | 'system',
  language: 'en',
  notifications: {
    telegram: true,
    apiLimits: true,
    securityAlerts: true,
    featureUpdates: false,
  },
  privacy: {
    shareUsageStatistics: false,
    allowAnalytics: false,
  },
  ui: {
    compactMode: false,
    showAdvancedFeatures: false,
    defaultToolView: 'grid' as 'grid' | 'list',
    autoSaveInputs: true,
  },
});

// Computed
const userProfile = computed(() => authStore.user);
const quotaUsage = computed(() => authStore.quotaUsage);

// Tier-related computed properties
const userTier = computed(() => authStore.user?.tier || 0);

const allTiers = computed(() => [
  {
    level: 0,
    name: 'Basic Access',
    icon: '🚀',
    description: 'Get started with basic features',
    shortDescription: 'Basic features',
    benefits: ['Basic MCP tool access', 'Telegram integration', 'Limited rate limits'],
    rateLimit: '100 calls/hour',
  },
  {
    level: 1,
    name: 'Explorer',
    icon: '🔍',
    description: 'Enhanced access with verification',
    shortDescription: 'Email verified',
    benefits: ['Higher rate limits', 'Email notifications', 'Basic analytics'],
    rateLimit: '500 calls/hour',
  },
  {
    level: 2,
    name: 'Communicator',
    icon: '📱',
    description: 'Multi-channel verified user',
    shortDescription: 'Phone verified',
    benefits: ['SMS notifications', 'Priority support', 'Advanced features'],
    rateLimit: '1000 calls/hour',
  },
  {
    level: 3,
    name: 'Customizer',
    icon: '⚙️',
    description: 'Personalized experience',
    shortDescription: 'Preferences set',
    benefits: ['Custom preferences', 'Personalized dashboard', 'Theme options'],
    rateLimit: '2000 calls/hour',
  },
  {
    level: 4,
    name: 'Builder',
    icon: '🛠️',
    description: 'Create and manage bots',
    shortDescription: 'Bot creation',
    benefits: ['Custom bot creation', 'Advanced configurations', 'Bot analytics'],
    rateLimit: '5000 calls/hour',
  },
  {
    level: 5,
    name: 'Verified Pro',
    icon: '✅',
    description: 'Identity verified professional',
    shortDescription: 'Identity verified',
    benefits: ['Identity verification badge', 'Professional features', 'API access'],
    rateLimit: '10000 calls/hour',
  },
  {
    level: 6,
    name: 'Premium',
    icon: '💳',
    description: 'Premium features unlocked',
    shortDescription: 'Payment added',
    benefits: ['Premium features', 'Advanced analytics', 'Priority processing'],
    rateLimit: '25000 calls/hour',
  },
  {
    level: 7,
    name: 'Elite',
    icon: '💎',
    description: 'Maximum tier with all features',
    shortDescription: 'All features',
    benefits: ['All features unlocked', 'Unlimited usage', 'Enterprise support'],
    rateLimit: 'Unlimited',
  },
]);

const currentTierInfo = computed(
  () => allTiers.value.find(tier => tier.level === userTier.value) || allTiers.value[0]
);

const nextTierInfo = computed(() => {
  const nextLevel = userTier.value + 1;
  if (nextLevel > 7) return null;

  const nextTier = allTiers.value.find(tier => tier.level === nextLevel);
  if (!nextTier) return null;

  // Get requirements for next tier
  const requirements = getRequirementsForTier(nextLevel);

  return {
    ...nextTier,
    requirements,
  };
});

const nextTierProgress = computed(() => {
  if (!nextTierInfo.value) return 100;

  const requirements = nextTierInfo.value.requirements;
  const completedCount = requirements.filter(req => req.completed).length;
  const totalCount = requirements.length;

  return totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
});

const canVerifyEmail = computed(() => {
  return !userProfile.value?.emailVerified && userTier.value >= 0;
});

const canVerifyPhone = computed(() => {
  return !userProfile.value?.phoneVerified && userTier.value >= 1;
});

const hasSetPreferences = computed(() => {
  const user = userProfile.value;
  return !!(user?.preferences && user.preferences.language && user.preferences.timezone);
});

const hasPaymentMethod = computed(() => {
  const user = userProfile.value;
  return !!(user?.paymentMethods && user.paymentMethods.length > 0);
});

const userDisplayName = computed(() => {
  if (!userProfile.value) return 'User';

  if (userProfile.value.firstName && userProfile.value.lastName) {
    return `${userProfile.value.firstName} ${userProfile.value.lastName}`;
  }

  return userProfile.value.firstName || userProfile.value.username || 'User';
});

const userInitials = computed(() => {
  if (!userProfile.value) return 'U';

  const firstName = userProfile.value.firstName || '';
  const lastName = userProfile.value.lastName || '';

  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }

  if (firstName) {
    return firstName[0].toUpperCase();
  }

  if (userProfile.value.username) {
    return userProfile.value.username[0].toUpperCase();
  }

  return 'U';
});

// Theme options
const themes = [
  {
    value: 'light',
    label: 'Light',
    description: 'Light theme',
    icon: {
      template: `
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      `,
    },
  },
  {
    value: 'dark',
    label: 'Dark',
    description: 'Dark theme',
    icon: {
      template: `
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      `,
    },
  },
  {
    value: 'system',
    label: 'System',
    description: 'Follow system',
    icon: {
      template: `
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      `,
    },
  },
];

// Methods
const loadUserData = () => {
  if (userProfile.value) {
    profileForm.firstName = userProfile.value.firstName || '';
    profileForm.lastName = userProfile.value.lastName || '';
    profileForm.username = userProfile.value.username || '';
  }

  // Load preferences from storage
  const storedPreferences = storageService.getUserPreferences();
  Object.assign(preferences, storedPreferences);
};

const saveAllSettings = async () => {
  isSaving.value = true;

  try {
    // Save profile updates
    if (hasProfileChanges()) {
      await authStore.updateProfile({
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        username: profileForm.username,
      });
    }

    // Save preferences to storage
    storageService.storeUserPreferences(preferences);

    // Apply theme change
    if (preferences.theme !== storageService.getTheme()) {
      storageService.setTheme(preferences.theme);
      applyTheme();
    }

    hasUnsavedChanges.value = false;
    window.notifications?.success('Settings saved successfully');
  } catch (error) {
    console.error('Failed to save settings:', error);
    window.notifications?.error('Failed to save settings');
  } finally {
    isSaving.value = false;
  }
};

const hasProfileChanges = (): boolean => {
  return (
    profileForm.firstName !== (userProfile.value?.firstName || '') ||
    profileForm.lastName !== (userProfile.value?.lastName || '') ||
    profileForm.username !== (userProfile.value?.username || '')
  );
};

const triggerPhotoUpload = () => {
  photoUploadInput.value?.click();
};

const handlePhotoUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0];

  if (!file) return;

  // Validate file type
  if (!file.type.startsWith('image/')) {
    window.notifications?.error('Please select a valid image file');
    return;
  }

  // Validate file size (5MB max)
  if (file.size > 5 * 1024 * 1024) {
    window.notifications?.error('Image size must be less than 5MB');
    return;
  }

  try {
    const formData = new FormData();
    formData.append('photo', file);

    await authStore.uploadProfilePhoto(formData);
    window.notifications?.success('Profile photo updated successfully');
  } catch (error) {
    console.error('Failed to upload photo:', error);
    window.notifications?.error('Failed to upload profile photo');
  }
};

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const exportAccountData = async () => {
  try {
    const data = {
      profile: userProfile.value,
      preferences: preferences,
      exportDate: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: 'application/json',
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mcp-account-data-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    window.notifications?.success('Account data exported successfully');
  } catch (error) {
    console.error('Failed to export account data:', error);
    window.notifications?.error('Failed to export account data');
  }
};

const deleteAccount = async () => {
  if (!showDeleteConfirmation.value) return;

  isDeleting.value = true;

  try {
    await authStore.deleteAccount();
    window.notifications?.success('Account deleted successfully');
    router.push({ name: 'auth' });
  } catch (error) {
    console.error('Failed to delete account:', error);
    window.notifications?.error('Failed to delete account');
  } finally {
    isDeleting.value = false;
    showDeleteConfirmation.value = false;
  }
};

const applyTheme = () => {
  const theme = preferences.theme;

  if (theme === 'system') {
    // Follow system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  } else if (theme === 'dark') {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
};

// Watch for changes
watch(
  [profileForm, preferences],
  () => {
    hasUnsavedChanges.value = true;
  },
  { deep: true }
);

// Listen for system theme changes
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
mediaQuery.addEventListener('change', () => {
  if (preferences.theme === 'system') {
    applyTheme();
  }
});

// Tier-related functions
function getRequirementsForTier(tierLevel: number) {
  const user = userProfile.value;
  const requirements = [];

  switch (tierLevel) {
    case 1:
      requirements.push({
        key: 'email_verified',
        title: 'Email Verification',
        description: 'Verify your email address',
        completed: user?.emailVerified || false,
        actionable: !user?.emailVerified,
        actionType: 'verify',
        actionLabel: 'Verify Email',
        statusText: user?.emailVerified ? 'Verified' : 'Not verified',
      });
      break;
    case 2:
      requirements.push({
        key: 'phone_verified',
        title: 'Phone Verification',
        description: 'Verify your phone number',
        completed: user?.phoneVerified || false,
        actionable: !user?.phoneVerified,
        actionType: 'verify',
        actionLabel: 'Verify Phone',
        statusText: user?.phoneVerified ? 'Verified' : 'Not verified',
      });
      break;
    case 3:
      requirements.push({
        key: 'preferences_set',
        title: 'Set Preferences',
        description: 'Configure your account preferences',
        completed: hasSetPreferences.value,
        actionable: !hasSetPreferences.value,
        actionType: 'configure',
        actionLabel: 'Set Preferences',
        statusText: hasSetPreferences.value ? 'Configured' : 'Not configured',
      });
      break;
    case 4:
      requirements.push({
        key: 'create_bot',
        title: 'Create a Bot',
        description: 'Create your first custom bot',
        completed: (user?.bots?.length || 0) > 0,
        actionable: false,
        actionType: 'create',
        actionLabel: 'Create Bot',
        statusText: (user?.bots?.length || 0) > 0 ? 'Created' : 'No bots created',
      });
      break;
    case 5:
      requirements.push({
        key: 'identity_verified',
        title: 'Identity Verification',
        description: 'Verify your identity',
        completed: user?.identityVerification?.verified || false,
        actionable: false,
        actionType: 'verify',
        actionLabel: 'Verify Identity',
        statusText: user?.identityVerification?.verified ? 'Verified' : 'Not verified',
      });
      break;
    case 6:
      requirements.push({
        key: 'payment_added',
        title: 'Add Payment Method',
        description: 'Add a payment method to your account',
        completed: hasPaymentMethod.value,
        actionable: false,
        actionType: 'add',
        actionLabel: 'Add Payment',
        statusText: hasPaymentMethod.value ? 'Added' : 'No payment method',
      });
      break;
    case 7:
      requirements.push({
        key: 'spending_threshold',
        title: 'Spending Threshold',
        description: 'Reach $100 in total spending',
        completed: (user?.spendingTotal || 0) >= 100,
        actionable: false,
        actionType: 'spend',
        actionLabel: 'Continue Using',
        statusText: `$${user?.spendingTotal || 0}/100 spent`,
      });
      break;
  }

  return requirements;
}

function handleRequirementAction(requirement: any) {
  switch (requirement.actionType) {
    case 'verify':
      if (requirement.key === 'email_verified') {
        showEmailVerificationModal.value = true;
      } else if (requirement.key === 'phone_verified') {
        showPhoneVerificationModal.value = true;
      }
      break;
    case 'configure':
      if (requirement.key === 'preferences_set') {
        // Scroll to preferences section
        const preferencesSection = document.querySelector('.card:nth-of-type(3)');
        preferencesSection?.scrollIntoView({ behavior: 'smooth' });
      }
      break;
  }
}

async function handleEmailVerified(email: string) {
  showEmailVerificationModal.value = false;

  // Show inline success message
  emailVerificationMessage.value = 'Email verified successfully!';
  showEmailVerificationSuccess.value = true;

  // Show main success banner
  verificationSuccessTitle.value = 'Email Verified Successfully!';
  verificationSuccessMessage.value = `Your email address ${email} has been verified. You now have access to enhanced features.`;
  showVerificationSuccess.value = true;

  try {
    await authStore.refreshUserData();
    await loadUserData();
    console.log('Email verified successfully:', email);

    // Hide inline success message after 3 seconds
    setTimeout(() => {
      showEmailVerificationSuccess.value = false;
      emailVerificationMessage.value = '';
    }, 3000);

    // Hide main banner after 10 seconds
    setTimeout(() => {
      showVerificationSuccess.value = false;
    }, 10000);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
    emailVerificationMessage.value = 'Verification successful but failed to refresh data';
    verificationSuccessMessage.value =
      'Your email was verified, but there was an issue refreshing your profile. Please refresh the page.';
    setTimeout(() => {
      showEmailVerificationSuccess.value = false;
      emailVerificationMessage.value = '';
    }, 3000);
  }
}

async function handlePhoneVerified(phoneNumber: string) {
  showPhoneVerificationModal.value = false;

  // Show inline success message
  phoneVerificationMessage.value = 'Phone verified successfully!';
  showPhoneVerificationSuccess.value = true;

  // Show main success banner
  verificationSuccessTitle.value = 'Phone Verified Successfully!';
  verificationSuccessMessage.value = `Your phone number ${phoneNumber} has been verified. You now have access to enhanced security features.`;
  showVerificationSuccess.value = true;

  try {
    await authStore.refreshUserData();
    await loadUserData();
    console.log('Phone verified successfully:', phoneNumber);

    // Hide inline success message after 3 seconds
    setTimeout(() => {
      showPhoneVerificationSuccess.value = false;
      phoneVerificationMessage.value = '';
    }, 3000);

    // Hide main banner after 10 seconds
    setTimeout(() => {
      showVerificationSuccess.value = false;
    }, 10000);
  } catch (error) {
    console.error('Failed to refresh user data:', error);
    phoneVerificationMessage.value = 'Verification successful but failed to refresh data';
    verificationSuccessMessage.value =
      'Your phone was verified, but there was an issue refreshing your profile. Please refresh the page.';
    setTimeout(() => {
      showPhoneVerificationSuccess.value = false;
      phoneVerificationMessage.value = '';
    }, 3000);
  }
}

// Helper functions
function hideVerificationSuccess() {
  showVerificationSuccess.value = false;
}

// Lifecycle
onMounted(() => {
  loadUserData();
  applyTheme();
});
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}
</style>
